import * as dates from '@simplybenefits/sb-dates';
import * as users from '../xhr/users.js';
import * as types from './types/users.js';
import * as adjudicationTypes from './types/adjudication.js';
import * as notifier from './notifier.js';
import store from '../redux/store.js';
import * as loader from './loader.js';

/**
 * @param dispatch
 * @param isLoading
 * @param type
 */
function setLoader(dispatch, isLoading, type = 'full') {
    dispatch(loader.setLoader({
        isLoading,
        type
    }));
}

/**
 *
 * @param {*} query
 * @param {*} nextCursor
 */
export function getUsers(query = '', nextCursor = false) {
    return (dispatch) => {
        const cursor = nextCursor === false ? store.getState().users.cursor : Number(nextCursor);
        if (cursor < 0) {
            return false;
        }

        return users
            .getAll(cursor, query)
            .then((data) => {
                dispatch({
                    type: types.GET_USERS_SUCCESS,
                    users: data.users,
                    cursor: data.users.length === 0 ? -1 : data.cursor,
                    shouldAppend: cursor > 0
                });

                return true;
            })
            .catch((error) => {
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param skipGlobalState
 * @returns {function(*): Promise<unknown | boolean>}
 */
export function getUser(userID, skipGlobalState = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .get(userID)
            .then((data) => {
                if (!skipGlobalState) {
                    dispatch({
                        type: types.GET_USER_SUCCESS,
                        currentUser: data
                    });
                }

                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} filters
 * @param {*} sort
 * @param {*} cursor
 * @returns
 */
export function getClaims(userID, filters = {}, sort = false, cursor = 0) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getClaims(userID, filters, sort, cursor)
            .then((claimsData) => {
                setLoader(dispatch, false);

                dispatch({
                    type: types.GET_CLAIMS_SUCCESS,
                    claims: claimsData,
                    shouldAppend: cursor > 0,
                    cursor: claimsData.length < 10 ? -1 : cursor + 10
                });
            })
            .catch((error) => {
                setLoader(dispatch, false);
                notifier.displayError(error.verbiage);
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} filters
 * @param {*} sort
 * @returns
 */
export function getHSAClaims(userID, filters = {}, sort = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getHSAClaims(userID, filters, sort)
            .then((claimsData) => {
                setLoader(dispatch, false);

                dispatch({
                    type: types.GET_HSA_CLAIMS_SUCCESS,
                    claims: claimsData
                });
            })
            .catch((error) => {
                setLoader(dispatch, false);
                notifier.displayError(error.verbiage);
            });
    };
}

/**
 *
 * @param {*} cert
 * @returns
 */
export function getUserByCert(cert) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getUserByCert(cert)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} filters
 * @param {*} sort
 * @returns
 */
export function getLSAClaims(userID, filters = {}, sort = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getLSAClaims(userID, filters, sort)
            .then((claimsData) => {
                setLoader(dispatch, false);

                dispatch({
                    type: types.GET_LSA_CLAIMS_SUCCESS,
                    claims: claimsData
                });
            })
            .catch((error) => {
                setLoader(dispatch, false);
                notifier.displayError(error.verbiage);
            });
    };
}

/**
 * @param cert
 * @param claimID
 * @returns {function(*): Promise<* | boolean>}
 */
export function getClaimCertMeta(cert, claimID = null) {
    return (dispatch) => {
        setLoader(dispatch, true);
        
        return users
            .getClaimCertMeta(cert, claimID)
            .then((data) => {
                const claimsBlocked = data?.meta?.accountClaimsBlocked || data?.meta?.divisionClaimsBlocked || data?.meta?.memberClaimsBlocked;

                if (claimsBlocked) {
                    setLoader(dispatch, false);
                    dispatch(notifier.displayError({ verbiage: 'This member’s claims are currently blocked. This includes claim adjudication and claims submissions from all users and sources.' }));
                    return false;
                }

                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} claimID
 */
export function getClaim(userID, claimID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getClaim(userID, claimID)
            .then((data) => {
                dispatch({
                    type: types.GET_USER_CLAIM_SUCCESS,
                    claimDetails: data
                });

                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} dependent
 */
export function addDependent(userID, dependent) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .addDependent(userID, dependent)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (dependent.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.ADD_DEPENDENT_SUCCESS,
                        spouseCoverage: dependent.spouseCoverage,
                        dependent: data
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Dependent added successfully!'));

                // reload the page to get up to date data in the user profile
                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} dependentID
 * @param {*} dependent
 */
export function editDependent(userID, dependentID, dependent) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .editDependent(userID, dependentID, dependent)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (dependent.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.UPDATE_DEPENDENT_SUCCESS,
                        userID,
                        dependentID,
                        spouseCoverage: dependent.spouseCoverage,
                        dependent: data
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Dependent updated successfully!'));

                // reload the page to get up to date data in the user profile
                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param parentID
 * @param body
 * @returns {function(*): Promise<boolean>}
 */
export function resetDependentPassword(parentID, body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .sendDependentForgotPassword(parentID, body)
            .then(() => {
                dispatch(notifier.displayMessage('Inform the user to check their inbox for a recovery email.'));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param dependentID
 * @param effectiveDate
 * @returns {function(*=): Promise<unknown>}
 */
export function removeDependent(userID, dependentID, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .removeDependent(userID, dependentID, effectiveDate)
            .then(() => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (effectiveDate <= currentDate) {
                    dispatch({
                        type: types.REMOVE_DEPENDENT_SUCCESS,
                        dependentID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Dependent removed successfully!'));

                // reload the page to get up to date data in the user profile
                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param dependentID
 * @returns {function(*): Promise<boolean>}
 */
export function getDependentTerminatedBenefits(userID, dependentID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getDependentTerminatedBenefits(userID, dependentID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param dependentID
 * @param benefitsToTerminate
 * @param effectiveDate
 */
export function terminateDependentBenefits(userID, dependentID, benefitsToTerminate, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .terminateDependentBenefits(userID, dependentID, benefitsToTerminate, effectiveDate)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param dependentID
 * @param benefitsToReinstate
 * @param effectiveDate
 */
export function reinstateDependentBenefits(userID, dependentID, benefitsToReinstate, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .reinstateDependentBenefits(userID, dependentID, benefitsToReinstate, effectiveDate)
            .then(() => {
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param beneficiary
 * @param currentBeneficiaries
 * @param isContingent
 * @returns {function(*=): Promise<unknown>}
 */
export function addBeneficiary(userID, beneficiary, currentBeneficiaries, isContingent = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .addBeneficiary(userID, beneficiary, currentBeneficiaries)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (beneficiary.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.ADD_BENEFICIARY_SUCCESS,
                        beneficiary: data,
                        isContingent
                    });

                    dispatch({
                        type: types.UPDATE_CURRENT_BENEFICIARIES,
                        currentBeneficiaries,
                        isContingent
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Beneficiary added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param beneficiaryID
 * @param beneficiary
 * @param currentBeneficiaries
 * @param isContingent
 * @returns {function(*=): Promise<unknown>}
 */
export function editBeneficiary(userID, beneficiaryID, beneficiary, currentBeneficiaries, isContingent = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .editBeneficiary(userID, beneficiaryID, beneficiary, currentBeneficiaries)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (beneficiary.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.UPDATE_BENEFICIARY_SUCCESS,
                        userID,
                        beneficiaryID,
                        beneficiary: data,
                        isContingent
                    });

                    dispatch({
                        type: types.UPDATE_CURRENT_BENEFICIARIES,
                        currentBeneficiaries,
                        isContingent
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Beneficiary updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param beneficiaryID
 * @param currentBeneficiaries
 * @param effectiveDate
 * @param isContingent
 * @returns {function(*=): Promise<boolean>}
 */
export function removeBeneficiary(userID, beneficiaryID, currentBeneficiaries, effectiveDate, isContingent = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .removeBeneficiary(userID, beneficiaryID, currentBeneficiaries, effectiveDate)
            .then(() => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (effectiveDate <= currentDate) {
                    dispatch({
                        type: types.REMOVE_BENEFICIARY_SUCCESS,
                        beneficiaryID,
                        isContingent
                    });

                    dispatch({
                        type: types.UPDATE_CURRENT_BENEFICIARIES,
                        currentBeneficiaries,
                        isContingent
                    });

                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Beneficiary removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} trustee
 */
export function addTrustee(userID, trustee) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .addTrustee(userID, trustee)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (trustee.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.ADD_TRUSTEE_SUCCESS,
                        trustee: data
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Trustee added successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param {*} userID
 * @param {*} trusteeID
 * @param {*} trustee
 */
export function editTrustee(userID, trusteeID, trustee) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .editTrustee(userID, trusteeID, trustee)
            .then((data) => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (trustee.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.UPDATE_TRUSTEE_SUCCESS,
                        userID,
                        trusteeID,
                        trustee: data
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Trustee updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param trusteeID
 * @param effectiveDate
 * @returns {function(*=): Promise<boolean>}
 */
export function removeTrustee(userID, trusteeID, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .removeTrustee(userID, trusteeID, effectiveDate)
            .then(() => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (effectiveDate <= currentDate) {
                    dispatch({
                        type: types.REMOVE_TRUSTEE_SUCCESS,
                        trusteeID
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Trustee removed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param body
 * @param isProfile
 * @returns {function(*=): Promise<boolean>}
 */
export function editUser(userID, body, isProfile = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .editUser(userID, body, isProfile)
            .then(() => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (body.effectiveDate <= currentDate) {
                    dispatch({
                        type: types.UPDATE_USER_SUCCESS,
                        userID,
                        body
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('User updated successfully!'));

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param accountID
 * @param effectiveDate
 * @returns {function(*=): Promise<unknown>}
 */
export function terminateUser(userID, accountID, effectiveDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .terminateUser(userID, accountID, effectiveDate)
            .then(() => {
                const currentDate = dates.getUTC(dates.getLocalDisplay(new Date(), 'YYYY/MM/DD'));

                // don't save in local state if it's a future dated change, we don't want them to submit it again and have the changes applied now
                if (effectiveDate <= currentDate) {
                    dispatch({
                        type: types.TERMINATE_USER_SUCCESS
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('User Terminated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param expectedLeaveReturnDate
 * @returns {function(*=): Promise<boolean>}
 */
export function changeStatusReturnDate(userID, expectedLeaveReturnDate) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .changeStatusReturnDate(userID, expectedLeaveReturnDate)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Return date updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 */
export function clearSelectedClaim() {
    return (dispatch) => {
        dispatch({
            type: types.CLEAR_SELECTED_CLAIM
        });
    };
}

/**
 * @param password
 * @returns {function(*=): Promise<any | never>}
 */
export function changePassword(password) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .changePassword(password)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Password changed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param email
 */
export function resetPassword(body) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .sendForgotPassword(body)
            .then(() => {
                dispatch(notifier.displayMessage('Inform the user to check their inbox for a recovery email.'));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 */
export function sendMagicLink(userID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .sendMagicLink(userID)
            .then(() => {
                dispatch(notifier.displayMessage('Inform the user to check their inbox for a recovery email.'));
                setLoader(dispatch, false);
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 *
 * @param userID
 * @param note
 * @param claimID
 * @returns {function(*=): (Q.Promise<boolean> | * | undefined | Promise<any>)}
 */
export function addMemberNote(userID, note, claimID = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .addMemberNote(userID, note, claimID)
            .then((data) => {
                if (claimID) {
                    dispatch({
                        type: adjudicationTypes.ADD_MEMBER_NOTE,
                        data
                    });
                } else {
                    dispatch({
                        type: types.ADD_MEMBER_NOTE,
                        data
                    });
                }

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Note added successfully!'));

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param claimID
 * @param dateRange
 * @returns {function(*=): Promise<boolean>}
 */
export function getMemberNotes(userID, claimID = false, dateRange = false) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getMemberNotes(userID, claimID, dateRange)
            .then((notes) => {
                dispatch({
                    type: types.GET_MEMBER_NOTES,
                    notes
                });

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param invoiceNum
 * @returns {function(*=): (Q.Promise<any> | * | undefined | Promise<void>)}
 */
export function getInvoice(userID, invoiceNum) {
    return (dispatch) => users
        .getInvoice(userID, invoiceNum)
        .then((data) => data)
        .catch((error) => {
            dispatch(notifier.displayError(error));
        });
}

/**
 * Mark the invoice as failed and apply a carry forward
 * @param userID
 * @param invoiceNum
 * @returns {function(*=): (Q.Promise<any> | * | undefined | Promise<void>)}
 */
export function applyInvoiceCarryForward(userID, invoiceNum) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .applyInvoiceCarryForward(userID, invoiceNum)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Invoice updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * Mark the invoice pending so we can re-pull it
 * @param userID
 * @param invoiceNum
 * @returns {function(*=): (Q.Promise<any> | * | undefined | Promise<void>)}
 */
export function resetInvoiceToPending(userID, invoiceNum) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .resetInvoiceToPending(userID, invoiceNum)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Invoice updated successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param benefit
 * @param effectiveDate
 * @param action
 * @returns {function(*): Promise<boolean>}
 */
export function updateBenefitStatus(userID, benefit, effectiveDate, action) {
    return (dispatch) => {
        setLoader(dispatch, true);        
        return users.updateBenefitStatus(userID, benefit, effectiveDate, action)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_USER_BENEFIT_STATUS,
                    data
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Benefit updated successfully!'));
                return true; 
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param benefit
 * @param effectiveDate
 * @param action
 * @returns {function(*): Promise<boolean>}
 */
export function updateBenefitPremium(userID, benefit, effectiveDate, action) {
    return (dispatch) => {
        setLoader(dispatch, true);        
        return users.updateBenefitPremium(userID, benefit, effectiveDate, action)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_USER_BENEFIT_PREMIUMS,
                    data
                });
                    
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Benefit updated successfully!'));
                return true; 
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param effectiveDate
 * @param action
 * @returns {function(*): Promise<boolean>}
 */
export function updateAllBenefitPremiums(userID, effectiveDate, action) {
    return (dispatch) => {
        setLoader(dispatch, true);        
        return users.updateAllBenefitPremiums(userID, effectiveDate, action)
            .then((data) => {
                dispatch({
                    type: types.UPDATE_USER_BENEFIT_PREMIUMS,
                    data
                });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Benefits updated successfully!'));
                return true; 
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param coverageHistoryID
 * @returns {function(*): Promise<unknown>}
 */
export function getUserCoverageHistory(userID, coverageHistoryID) {
    return (dispatch) => {
        setLoader(dispatch, true); 
        return users.getUserCoverageHistory(userID, coverageHistoryID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param coverageEndDate
 * @returns {function(*): Promise<unknown>}
 */
export function getDependentsAtCoverageEndDate(userID, coverageEndDate) {
    return (dispatch) => {
        setLoader(dispatch, true); 
        return users.getDependentsAtCoverageEndDate(userID, coverageEndDate)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param product
 * @param volume
 * @param member
 * @param entityType
 */
export function getMarketplaceProductRate(product, volume, member, entityType = 'primary') {
    return users
        .getMarketplaceProductRate(product.productID, {
            volume,
            entityType,
            pricing: product.pricing,
            ratesType: product.ratesType,
            commissionPercentage: product.commissionPercentage,
            markup: product.markup,
            coverageType: member.coverageType,
            birthdate: member.birthdate,
            isSmoker: Boolean(member.isSmoker),
            hasDependentSmoker: Boolean(member.hasDependentSmoker),
            gender: member.gender
        })
        .then((data) => data.rate)
        .catch((error) => {
            console.log(error);
        });
}

/**
 * @param userID
 * @param productID
 * @returns {function(*): Promise<*>}
 */
export function getMarketplaceProduct(userID, productID) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .getMarketplaceProduct(userID, productID)
            .then((data) => {
                setLoader(dispatch, false);
                return data;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * @param userID
 * @param productID
 * @param failedKnockOutQuestions
 * @returns {function(*): *}
 */
export function blockProductCoverage(userID, productID, failedKnockOutQuestions) {
    return (dispatch) => users
        .blockMarketplaceProductCoverage(userID, productID, failedKnockOutQuestions)
        .then((coverageTypesBlocked) => coverageTypesBlocked)
        .catch((error) => {
            dispatch(notifier.displayError(error));
            return false;
        });
}

/**
 * @param userID
 * @param purchaseData
 * @returns {function(*): *}
 */
export function purchaseMarketplaceProduct(userID, purchaseData) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .purchaseMarketplaceProduct(userID, purchaseData)
            .then(() => {
                // TODO:
                // dispatch({
                //     type: types.SET_PRODUCTS,
                //     data
                // });

                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Purchase completed successfully!'));
                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param productID
 * @param signage
 * @returns {function(*): *}
 */
export function terminateMarketplaceProduct(userID, productID, signage) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .terminateMarketplaceProduct(userID, productID, signage)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Product terminated successfully!'));

                setTimeout(() => {
                    window.location.reload();
                }, 2000);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}

/**
 * @param userID
 * @param maxAndBalanceData
 * @returns {function(*): Promise<boolean>}
 */
export function setAllotmentAndBalance(userID, maxAndBalanceData) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return users.setAllotmentAndBalance(userID, maxAndBalanceData)
            .then((data) => {
                setLoader(dispatch, false);

                // No change Recorded (Amounts did not change but set pendingMaximumSetting 0)
                if (data) {
                    dispatch(notifier.displayError({ verbiage: `No change recorded to HSA or LSA amounts.` }));
                } else {
                    dispatch(notifier.displayMessage('Spending account allotment and balances update successfully!'));
                }

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
                
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
            });
    };
}

/**
 * 
 * @param {*} userID 
 * @returns 
 */
export function triggerPooledAllotment(userID) {
    return (dispatch) => {
        setLoader(dispatch, true);
        return users.triggerPooledAllotment(userID)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage('Allotment trigger set successfully!'));
                return true;
            });
    };
}

/**
 * @param {*} userID 
 * @param {*} claimsBlocked 
 * @returns 
 */
export function setClaimsBlockedStatus(userID, claimsBlocked) {
    return (dispatch) => {
        setLoader(dispatch, true);

        return users
            .setClaimsBlockedStatus(userID, claimsBlocked)
            .then(() => {
                setLoader(dispatch, false);
                dispatch(notifier.displayMessage(claimsBlocked ? 'Successfully blocked claims!' : 'Successfully unblocked claims!'));

                setTimeout(() => {
                    window.location.reload();
                }, 500);

                return true;
            })
            .catch((error) => {
                setLoader(dispatch, false);
                dispatch(notifier.displayError(error));
                return false;
            });
    };
}